import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types/movie';
import { CATEGORY_LABELS } from '@/types/movie';

export default function HeroCarousel({ items }: { items: Movie[] }) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const active = items[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      <img
        key={active.id}
        src={active.banner || active.poster}
        alt={`${active.title} 焦点推荐`}
        title={`${active.title} - 妖精视频热门推荐`}
        className="absolute inset-0 w-full h-full object-cover object-top animate-fade-up"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/10"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/20"></div>

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-line"></i>
            本周焦点推荐
          </span>
          <h2 className="mt-4 font-heading text-3xl md:text-5xl font-extrabold text-background-50 leading-tight">
            {active.title}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-100/90">
            <span className="rounded bg-background-50/15 px-2 py-0.5">{CATEGORY_LABELS[active.category]}</span>
            <span>{active.year}</span>
            <span>{active.region}</span>
            <span>{active.duration}</span>
            <span className="inline-flex items-center gap-1 font-semibold text-accent-300">
              <i className="ri-star-fill"></i>
              {active.rating.toFixed(1)}
            </span>
          </div>
          <p className="mt-4 hidden sm:block text-sm md:text-base leading-relaxed text-background-100/85 line-clamp-3">
            {active.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              查看详情
            </Link>
            <Link
              to={`/category/${active.category}`}
              className="whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              更多{CATEGORY_LABELS[active.category]}
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 z-10 flex items-center gap-4">
        <button
          type="button"
          aria-label="上一部"
          onClick={() => setIndex((i) => (i - 1 + count) % count)}
          className="w-9 h-9 rounded-full border border-background-50/40 bg-foreground-950/30 text-background-50 flex items-center justify-center hover:bg-foreground-950/60 transition-colors"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => setIndex((i) => (i + 1) % count)}
          className="w-9 h-9 rounded-full border border-background-50/40 bg-foreground-950/30 text-background-50 flex items-center justify-center hover:bg-foreground-950/60 transition-colors"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>

      <div className="absolute bottom-7 left-4 md:left-8 z-10 flex items-center gap-2">
        {items.map((m, i) => (
          <button
            key={m.id}
            type="button"
            aria-label={`切换到 ${m.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-8 bg-accent-400' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}